import axios from 'axios'

/**
 * CSRF の合言葉を、確実に持っている状態にする。
 *
 * ## なぜ要るのか
 *
 * サーバは、どの要求でも合言葉の写し（`XSRF-TOKEN` Cookie）を出す。
 * axios は同一オリジンなら、その Cookie を読んで `X-XSRF-TOKEN` 見出しへ
 * 自動で付ける。**だから普通は何もしなくても通る。**
 *
 * でも「普通は」で済ませない。
 *
 * - いちばん最初の要求がいきなり `POST`（ログイン）だと、まだ写しが無い
 * - サーバを入れ替えるとセッションが消え、写しが意味を失う
 * - 利用者が Cookie を消すこともある
 *
 * どれも、**通信は届くのに全部 403 になる**。しかも画面には
 * 「受け付けられませんでした」としか出ないので、原因が分からない。
 *
 * ## 合言葉は秘密ではない
 *
 * 他所のサイトから読めないことだけが効いている。だから Cookie は
 * JavaScript から読める形で出ている。**記録や解析へ流さない**のは、
 * 秘密だからではなく、余計な経路を作らないため。
 */

const BASE = process.env.VUE_APP_API_ORIGIN

/** サーバが出す写しの名前。axios の既定に合わせてある。 */
export const CSRF_COOKIE_NAME = 'XSRF-TOKEN'

/** 合言葉が古いときにサーバが返す印。 */
export const CSRF_ERROR_CODE = 'CSRF_TOKEN_INVALID'

/** いま写しを持っているか。 */
export function hasCsrfToken () {
  return readCookie(CSRF_COOKIE_NAME) !== ''
}

/**
 * 合言葉の写しを取りに行く。
 *
 * **持っていても取りに行ってよい。**サーバは作り直さず、いまの値を
 * 出し直すだけなので、開いている別のタブが断られることはない。
 */
export async function ensureCsrfToken () {
  try {
    await axios.get(BASE + '/auth/csrf')
    return true
  } catch (error) {
    /*
     * ここで止めない。**取れなかっただけで画面を出さないほうが困る。**
     * 実際に POST するときに断られ、そこで取り直す。
     */
    return false
  }
}

/**
 * 断られたのが「合言葉が古いから」か。
 *
 * サーバは、それ以外の断りと文言を分けていない。**印だけで見分ける。**
 * 文言で見分けると、文言を直した日に静かに壊れる。
 */
export function isCsrfFailure (error) {
  const response = error && error.response
  if (!response || response.status !== 403) return false
  const data = response.data
  return Boolean(data) && data.code === CSRF_ERROR_CODE
}

function readCookie (name) {
  const prefix = name + '='
  const found = String(document.cookie || '')
    .split(';')
    .map(part => part.trim())
    .find(part => part.indexOf(prefix) === 0)
  return found ? decodeURIComponent(found.slice(prefix.length)) : ''
}
